import { skipToken, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { ClaimConfirmationModal } from '@/components/ClaimConfirmationModal';
import { ClaimSubmissionForm } from '@/components/ClaimSubmissionForm';
import { ErrorDisplay } from '@/components/ErrorDisplay';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Modal } from '@/components/Modal';
import { ReceiptUploadModal } from '@/components/ReceiptUploadModal';

import { useCategories } from '@/hooks/useCategories';
import { useUpdateClaim } from '@/hooks/useClaims';
import { useDashboardData } from '@/hooks/useDashboardData';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';

import { requestUploadUrl, uploadToS3, type UpdateClaimPayload } from '@/services/claims';
import { fetchDashboardData, type IDashboardData } from '@/services/dashboard';

import type { ICategory } from '@/types/category';
import type { IClaim, IClaimSubmission } from '@/types/claim';
import type { ReceiptAnalysisResults } from '@/types/receipt/receipt';

import { getClaimEditAdjustment } from '@/utils/claimUtils';

interface ClaimSubmissionModalProps {
  onClose: () => void;
  claim?: IClaim | null;
}

function ClaimSubmissionModal({ onClose, claim: initialClaim = null }: ClaimSubmissionModalProps) {
  const isEdit = Boolean(initialClaim);
  const {
    data: flags,
    loading: flagsLoading,
    error: flagsError,
    refetch: refetchFlags,
  } = useFeatureFlags();
  const flagsReady = !flagsLoading && !flagsError;
  const receiptAiEnabled = flagsReady && Boolean(flags.receipt_auto_fill);
  const {
    data: categories,
    loading: categoriesLoading,
    error: categoriesError,
    refetch: refetchCategories,
  } = useCategories();
  const {
    data: dashboardData,
    loading: dashboardLoading,
    error: dashboardError,
    refetch: refetchDashboard,
  } = useDashboardData();
  const { updateClaim, isUpdating, error: updateError } = useUpdateClaim();

  const [currentMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const claimMonth = initialClaim?.monthYear ?? initialClaim?.createdAt?.slice(0, 7) ?? null;
  const isCrossMonthEdit = isEdit && claimMonth !== null && claimMonth !== currentMonth;

  const { data: originalMonthDashboard, isLoading: originalMonthLoading } =
    useQuery<IDashboardData>({
      queryKey: ['dashboard', claimMonth],
      queryFn: isCrossMonthEdit && claimMonth ? () => fetchDashboardData(claimMonth) : skipToken,
    });

  // Receipt upload and analysis state
  const [showReceiptUpload, setShowReceiptUpload] = useState(false);
  const [skipReceiptAnalysis, setSkipReceiptAnalysis] = useState(false);
  const [analysisResults, setAnalysisResults] = useState<ReceiptAnalysisResults | null>(null);
  const [receiptFile, setReceiptFile] = useState<File | null>(null);

  useEffect(() => {
    if (isEdit) {
      // oxlint-disable-next-line react/set-state-in-effect
      setShowReceiptUpload(false);
      return;
    }
    if (!flagsReady) return;
    setShowReceiptUpload(receiptAiEnabled);
  }, [isEdit, flagsReady, receiptAiEnabled]);

  // Claim confirmation state
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingClaim, setPendingClaim] = useState<IClaimSubmission | null>(null);

  const handleAnalysisComplete = (results: ReceiptAnalysisResults, file: File) => {
    setAnalysisResults(results);
    setReceiptFile(file);
    setShowReceiptUpload(false);
  };

  const handleReceiptUploadCancel = () => {
    onClose();
  };

  const handleReupload = () => {
    setAnalysisResults(null);
    setReceiptFile(null);
    if (receiptAiEnabled) {
      setShowReceiptUpload(true);
    }
  };

  const handleFormSubmit = (claimData: IClaimSubmission, file: File) => {
    setPendingClaim(claimData);
    setReceiptFile(file);
    setShowConfirmModal(true);
  };

  const handleConfirmModalCancel = () => {
    setShowConfirmModal(false);
    setPendingClaim(null);
  };

  const handleSubmitEdit = async (
    payload: UpdateClaimPayload,
    attachments: { keptReceiptKeys: string[]; newFiles: File[] },
  ) => {
    if (!initialClaim) return;
    const newKeys: string[] = [];
    for (const file of attachments.newFiles) {
      const { uploadUrl, s3Key } = await requestUploadUrl(file.name, file.type);
      await uploadToS3(uploadUrl, file);
      newKeys.push(s3Key);
    }
    const receiptS3Keys = [...attachments.keptReceiptKeys, ...newKeys];
    const payloadToSend: UpdateClaimPayload = { ...payload, receiptS3Keys };
    await updateClaim({ claimId: initialClaim.id, payload: payloadToSend });
    onClose();
  };

  const loading =
    dashboardLoading ||
    categoriesLoading ||
    flagsLoading ||
    (isCrossMonthEdit && originalMonthLoading) ||
    (!isEdit && receiptAiEnabled && !showReceiptUpload && !analysisResults && !skipReceiptAnalysis);
  const error = dashboardError || categoriesError || (!isEdit ? flagsError : null);

  const selectedCategory = pendingClaim
    ? categories.find((cat: ICategory) => cat.id === pendingClaim.category)
    : null;

  const editAdjustment = getClaimEditAdjustment(
    isEdit,
    initialClaim,
    isCrossMonthEdit && originalMonthDashboard ? originalMonthDashboard : dashboardData,
  );

  // Show receipt upload modal first
  if (showReceiptUpload) {
    return (
      <ReceiptUploadModal
        onAnalysisComplete={handleAnalysisComplete}
        onCancel={handleReceiptUploadCancel}
        onEnterManually={(file) => {
          if (file) setReceiptFile(file);
          setShowReceiptUpload(false);
          setSkipReceiptAnalysis(true);
        }}
      />
    );
  }

  return (
    <>
      <Modal
        title={isEdit ? 'Edit claim' : 'New Claim'}
        onClose={onClose}
        maxWidth="md"
        zIndex={50}
        headerSticky
        titleSize="lg"
        titleFont="standard"
        closeOnEscape={false}
        closeOnBackdropClick={false}
      >
        {loading && (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        )}

        {error && (
          <ErrorDisplay
            error={error}
            onRetry={() => {
              void refetchCategories();
              void refetchDashboard();
              if (flagsError) void refetchFlags();
            }}
          />
        )}

        {updateError && (
          <p className="mb-4 rounded bg-red-50 p-3 text-sm text-red-600">{updateError}</p>
        )}

        {!loading && !error && dashboardData && (
          <ClaimSubmissionForm
            categories={categories as ICategory[]}
            currentBalance={dashboardData.balance + editAdjustment.balance}
            monthlyUsage={
              isCrossMonthEdit && originalMonthDashboard
                ? originalMonthDashboard.monthlyUsage - editAdjustment.monthlyUsage
                : dashboardData.monthlyUsage - editAdjustment.monthlyUsage
            }
            monthlyLimit={
              isCrossMonthEdit && originalMonthDashboard
                ? originalMonthDashboard.monthlyLimit
                : dashboardData.monthlyLimit
            }
            onSubmit={handleFormSubmit}
            mode={isEdit ? 'edit' : 'new'}
            initialClaim={initialClaim}
            onSubmitEdit={isEdit ? handleSubmitEdit : undefined}
            isSubmittingEdit={isUpdating}
            initialData={
              analysisResults
                ? {
                    description: analysisResults.description || undefined,
                    categoryId: analysisResults.categoryId || undefined,
                    amount: analysisResults.amount?.toString() || undefined,
                  }
                : undefined
            }
            preUploadedReceipt={receiptFile || undefined}
            onReupload={handleReupload}
            onCancelEdit={isEdit ? onClose : undefined}
          />
        )}
      </Modal>

      {showConfirmModal && pendingClaim && selectedCategory && receiptFile && (
        <ClaimConfirmationModal
          claimData={pendingClaim}
          categoryName={selectedCategory.name}
          receiptFile={receiptFile}
          onCancel={handleConfirmModalCancel}
          onSuccess={onClose}
        />
      )}
    </>
  );
}

export { ClaimSubmissionModal };
